import React, { useEffect, useRef, useState, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";

// App.js (ListeningStream)
const ListeningStream = () => {
  const items = [
    { flag: "🇵🇷", label: "Reggaeton", url: "https://open.spotify.com/playlist/4rCMVwM65yUOYLNRnuveB5?si=4b9be26713a64e8c" },
    { flag: "🇳🇱", label: "Dutch",     url: "https://open.spotify.com/playlist/7lOkVHFJ6ENsdejS3rpWyy?si=3e7209d4bb3143f4" },
    { flag: "🇯🇲", label: "Caribbean", url: "https://open.spotify.com/playlist/4qdNJ83AzBPqwt4ZI91bNv?si=0de99afedc154f4e" },
    { flag: "🇬🇧", label: "UK",        url: "https://open.spotify.com/playlist/5GfcySKqJnPHcb2m3k6oQF?si=a707853920c84079" },
  ];

  const listRef = useRef(null);
  const [shift, setShift] = useState(0);
  const SPEED_PX_PER_SEC = 60;

  // null-safe measurement
  const measure = useCallback((node) => {
    const el = node ?? listRef.current;
    if (!el) return;
    setShift(el.scrollWidth);
  }, []);

  useEffect(() => {
    // measure on next paint
    const rafId = requestAnimationFrame(() => measure());

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver((entries) => {
        const node = entries?.[0]?.target ?? listRef.current;
        measure(node);
      });
      if (listRef.current) ro.observe(listRef.current);
    } else {
      // fallback for older browsers
      const onResize = () => measure();
      window.addEventListener("resize", onResize);
      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", onResize);
      };
    }

    return () => {
      cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
    };
  }, [measure]);

  const duration = shift > 0 ? shift / SPEED_PX_PER_SEC : 8;

  return (
    <div className="mini-stream-wrap" aria-label="Currently listening">
      <div className="mini-stream-title">🎶 Currently Listening</div>

      <div className="mini-stream mini-stream--compact">
        <div className="mini-stream-viewport">
          <div
            className="mini-stream-track"
            style={{
              "--shift": `${shift}px`,
              "--duration": `${duration}s`,
            }}
          >
            <ul className="mini-stream-list" ref={listRef}>
              {items.map((it) => (
                <li key={it.label}>
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mini-chip"
                  >
                    <span className="flag">{it.flag}</span>
                    <span>{it.label}</span>
                  </a>
                </li>
              ))}
            </ul>

            {/* clone for seamless loop */}
            <ul className="mini-stream-list" aria-hidden="true">
              {items.map((it, i) => (
                <li key={`${it.label}-clone-${i}`}>
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mini-chip"
                  >
                    <span className="flag">{it.flag}</span>
                    <span>{it.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}; // ⬅️ keep this closing brace

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const testimonials = [
    {
      text:
        "As captain of the soccer team, Krishna led with heart and skill. Our team was made up of players from all kinds of backgrounds, and he had this rare ability to make everyone feel seen and valued. Under his leadership, the team grew more connected, disciplined, and confident both on and off the field.",
      author: "Coach Michael",
      role: "Varsity Soccer Coach",
    },
    {
      text:
        "What sets Krishna apart is his constant curiosity and drive to learn. He actively seeks new knowledge, asks insightful questions, and iterates with intention.",
      author: "Computer Science Mentor - Vikas Goyal",
    },
  ];

  return (
    <div className={`home-container ${isVisible ? "visible" : ""}`}>
      {/* HERO */}
      <section className="hero-section compact-hero">
        <div className="hero-content">
          {/* Left: intro text */}
          <div className="hero-text">
            <div className="subtitle-badge">
              <span>Entrepreneur</span>
              <span className="dot">·</span>
              <span>Tutor</span>
              <span className="dot">·</span>
              <span>Musician</span>
            </div>
            <h1 className="hero-title">
              Hi, I'm <span className="gradient-text">Krishna</span>
            </h1>
            <p className="hero-description">
              I'm a high school senior from Woodstock, Illinois, passionate about turning ideas into
              real-world projects, whether it's developing <strong>Navigo</strong> to help
              immigrants navigate airports, or <strong>Vendi</strong> to connect local food vendors
              with customers. Outside of coding, I enjoy creating music with my Akai MPK mini and
              love exploring global music, from Dutch pop to Jamaican reggae. I also lead an ESL
              tutoring program, where we help English Language Learners in a variety of subjects. Every project I take on starts with one question:{" "}
              <em>how can I make everyday life more inclusive and human?</em>
            </p>
            <div className="hero-buttons">
              <a
                href="https://www.linkedin.com/in/krishna-patel-325bb8258/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                data-testid="linkedin-btn"
              >
                LinkedIn
              </a>
              <a href="mailto:kpatel6607@gmail.com" className="btn-secondary" data-testid="email-btn">
                Email
              </a>
            </div>
          </div>

          {/* Right: mini listening bar + image + featured badge */}
          <div className="hero-image">
            <ListeningStream />
            <div className="image-wrapper">
              <img
                src={`${process.env.PUBLIC_URL}/static/img/headshot.png`}
                alt="Krishna Patel"
              />
            </div>
            <div className="featured-inline" aria-label="Featured in">
              <a
                href="https://www.thewoodstockindependent.com/2025/04/whs-students-picked-for-google-showcase/"
                target="_blank"
                rel="noopener noreferrer"
                className="featured-chip"
                data-testid="featured-inline-link"
              >
                📰 Featured in <strong>The Woodstock Independent</strong>
              </a>
              <span className="featured-sub">WHS Students Picked for Google Showcase</span>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS (moved up to appear in the first fold) */}
      <section className="testimonials-section first-fold" id="testimonials">
        <h2 className="section-title">What Do People Say About Me?</h2>
        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="glass-card testimonial-card"
              data-testid={`testimonial-${index}`}
            >
              <div className="quote-icon">"</div>
              <p className="testimonial-text">{testimonial.text}</p>
              <p className="testimonial-author">— {testimonial.author}</p>
            </div>
          ))}
        </div>
        <div className="testimonials-cta">
          <Link to="/testimonials" className="btn-primary" data-testid="see-all-testimonials-btn">
            See All Testimonials →
          </Link>
        </div>
      </section>
    </div>
  );
};

const MyWork = () => {
  // tabs: "projects" | "social" | "music"
  const [tab, setTab] = React.useState("projects");

  const [activeImageIndex, setActiveImageIndex] = React.useState({
    geoquest: 0,
    navigo: 0,
    vendi: 0,
    lexilift: 0,
    orbital: 0,
    dojobrawl: 0,
  });

const PUB = process.env.PUBLIC_URL || "";

const projectImages = {
  geoquest: [
    `${PUB}/static/img/geoquest.png`,
    `${PUB}/static/img/geoquest6.png`,
    `${PUB}/static/img/geoquest1.png`,
    `${PUB}/static/img/geoquest2.png`,
    `${PUB}/static/img/geoquest3.png`,
    `${PUB}/static/img/geoquest4.png`,
    `${PUB}/static/img/geoquest5.png`,
  ],

  navigo: [
    `${PUB}/static/img/Navigo.png`,
    `${PUB}/static/img/Navigo1.png`,
    `${PUB}/static/img/Navigo2.png`,
    `${PUB}/static/img/Navigo3.png`,
    `${PUB}/static/img/Navigo4.png`,
    `${PUB}/static/img/Navigo5.png`,
    `${PUB}/static/img/Navigo6.png`,
  ],
  vendi: [
    `${PUB}/static/img/Vendi1.png`,
    `${PUB}/static/img/Vendi2.png`,
    `${PUB}/static/img/Vendi3.png`,
  ],
  lexilift: [
    `${PUB}/static/img/Lexi1.png`,
    `${PUB}/static/img/Lexi2.png`,
    `${PUB}/static/img/Lexi3.png`,
    `${PUB}/static/img/Lexi4.png`,
    `${PUB}/static/img/Lexi5.png`,
    `${PUB}/static/img/Lexi6.png`,
    `${PUB}/static/img/Lexi7.png`,
    `${PUB}/static/img/Lexi8.png`,
  ],
  orbital: [
    `${PUB}/static/img/Ody1.png`,
    `${PUB}/static/img/Ody2.png`,
    `${PUB}/static/img/Ody3.png`,
  ],
  dojobrawl: [`${PUB}/static/videos/DojoBrawl.mp4`],

 
};

  const nextImage = (project) => {
    setActiveImageIndex((prev) => ({
      ...prev,
      [project]: (prev[project] + 1) % projectImages[project].length,
    }));
  };

  const prevImage = (project) => {
    setActiveImageIndex((prev) => ({
      ...prev,
      [project]:
        prev[project] === 0 ? projectImages[project].length - 1 : prev[project] - 1,
    }));
  };

  return (
    <div className="work-page">
      <div className="page-header">
        <Link to="/" className="back-link" data-testid="back-home-link">
          ← Back to Home
        </Link>
        <h1 className="page-title">My Work</h1>
        <p className="page-subtitle">Projects, Music, and Creative Endeavors</p>
      </div>

      {/* Toggle / Tabs */}
      <div
        className="work-toggle"
        role="tablist"
        aria-label="Choose a work category"
      >
        <button
          type="button"
          role="tab"
          id="tab-projects"
          aria-controls="panel-projects"
          aria-selected={tab === "projects"}
          className={`toggle-btn ${tab === "projects" ? "active" : ""}`}
          onClick={() => setTab("projects")}
        >
          💻 Coding Projects
        </button>
        <button
          type="button"
          role="tab"
          id="tab-social"
          aria-controls="panel-social"
          aria-selected={tab === "social"}
          className={`toggle-btn ${tab === "social" ? "active" : ""}`}
          onClick={() => setTab("social")}
        >
          📱 Social Media
        </button>
        <button
          type="button"
          role="tab"
          id="tab-music"
          aria-controls="panel-music"
          aria-selected={tab === "music"}
          className={`toggle-btn ${tab === "music" ? "active" : ""}`}
          onClick={() => setTab("music")}
        >
          🎵 Music
        </button>
      </div>

      {/* Panels */}
      {/* Coding Projects (default) */}
      {tab === "projects" && (
        <section
          id="panel-projects"
          role="tabpanel"
          aria-labelledby="tab-projects"
          className="work-section"
        >
          <h2 className="work-category-title">💻 Coding Projects</h2>
          <div className="work-grid coding-projects-grid">
            {/* GeoQuest */}
            <div className="glass-card work-card project-card-enhanced" data-testid="geoquest">
              <h3>GeoQuest</h3>
              <p>Website designed to help users master world geography through game and quizzes.</p>
              <div className="tech-stack">
                <span className="tech-tag">Python Flask</span>
                <span className="tech-tag">JavaScript</span>
                <span className="tech-tag">RapidAPI</span>
                <span className="tech-tag">Leaflet</span>
                <span className="tech-tag">CSS</span>
                <span className="tech-tag">HTML</span>
              </div>
              
              <div className="project-carousel">
                <button
                  className="carousel-btn prev-btn"
                  onClick={() => prevImage("geoquest")}
                  data-testid="geoquest-prev"
                >
                  ‹
                </button>
                <div className="carousel-image-container">
                  <img
                    src={projectImages.geoquest[activeImageIndex.geoquest]}
                    alt={`GeoQuest ${activeImageIndex.geoquest + 1}`}
                    className="carousel-image"
                  />
                  <div className="carousel-counter">
                    {activeImageIndex.geoquest + 1} / {projectImages.geoquest.length}
                  </div>
                </div>
                <button
                  className="carousel-btn next-btn"
                  onClick={() => nextImage("geoquest")}
                  data-testid="geoquest-next"
                >
                  ›
                </button>
              </div>
              <div className="work-links">
                <a
                  href="https://github.com/gershinho/geoquest"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  📂 GitHub
                </a>
              <a
                href="https://geoquest-production.up.railway.app"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                🌍 Live Site
              </a>
            </div>
           </div> 
            {/* Navigo */}
            <div className="glass-card work-card project-card-enhanced" data-testid="navigo">
              <h3>Navigo</h3>
              <p>
                A mobile app to help immigrants navigate airports with multilingual guidance and
                accessibility features.
              </p>
              <div className="tech-stack">
                <span className="tech-tag">React Native</span>
                <span className="tech-tag">Expo</span>
                <span className="tech-tag">Aero API</span>
                <span className="tech-tag">Python Flask</span>
                <span className="tech-tag">MappedIn</span>
              </div>
              <div className="project-carousel">
                <button
                  className="carousel-btn prev-btn"
                  onClick={() => prevImage("navigo")}
                  data-testid="navigo-prev"
                >
                  ‹
                </button>
                <div className="carousel-image-container">
                  <img
                    src={projectImages.navigo[activeImageIndex.navigo]}
                    alt={`Navigo ${activeImageIndex.navigo + 1}`}
                    className="carousel-image"
                  />
                  <div className="carousel-counter">
                    {activeImageIndex.navigo + 1} / {projectImages.navigo.length}
                  </div>
                </div>
                <button
                  className="carousel-btn next-btn"
                  onClick={() => nextImage("navigo")}
                  data-testid="navigo-next"
                >
                  ›
                </button>
              </div>

              <div className="work-links">
                <a
                  href="https://github.com/gershinho/Navigo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  📂 GitHub
                </a>
                <a
                  href="https://www.thewoodstockindependent.com/2025/04/whs-students-picked-for-google-showcase/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  📰 Featured
                </a>
              </div>
            </div>

            {/* Vendi */}
            <div className="glass-card work-card project-card-enhanced" data-testid="vendi">
              <h3>Vendi</h3>
              <p>
                Seller enters business details and books open spots to sell anywhere in the world |
                Users can see nearby food vendors.
              </p>
              <div className="tech-stack">
                <span className="tech-tag">JavaScript</span>
                <span className="tech-tag">Python Flask</span>
                <span className="tech-tag">Leaflet</span>
                <span className="tech-tag">CSS</span>
                <span className="tech-tag">HTML</span>
              </div>
              <div className="project-carousel">
                <button
                  className="carousel-btn prev-btn"
                  onClick={() => prevImage("vendi")}
                  data-testid="vendi-prev"
                >
                  ‹
                </button>
                <div className="carousel-image-container">
                  <img
                    src={projectImages.vendi[activeImageIndex.vendi]}
                    alt={`Vendi ${activeImageIndex.vendi + 1}`}
                    className="carousel-image"
                  />
                  <div className="carousel-counter">
                    {activeImageIndex.vendi + 1} / {projectImages.vendi.length}
                  </div>
                </div>
                <button
                  className="carousel-btn next-btn"
                  onClick={() => nextImage("vendi")}
                  data-testid="vendi-next"
                >
                  ›
                </button>
              </div>
              <div className="work-links">
                <a
                  href="https://github.com/gershinho/Vendi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  📂 GitHub
                </a>
              </div>
            </div>

            {/* LexiLift */}
            <div className="glass-card work-card project-card-enhanced" data-testid="lexilift">
              <h3>LexiLift</h3>
              <p>
                Vocabulary games with matching, fill-in-the-blank, difficulty levels, and
                achievements.
              </p>
              <div className="tech-stack">
                <span className="tech-tag">Python Flask</span>
                <span className="tech-tag">JavaScript</span>
                <span className="tech-tag">Merriam-Webster API</span>
                <span className="tech-tag">CSS</span>
                <span className="tech-tag">HTML</span>
              </div>
              <div className="project-carousel">
                <button
                  className="carousel-btn prev-btn"
                  onClick={() => prevImage("lexilift")}
                  data-testid="lexilift-prev"
                >
                  ‹
                </button>
                <div className="carousel-image-container">
                  <img
                    src={projectImages.lexilift[activeImageIndex.lexilift]}
                    alt={`LexiLift ${activeImageIndex.lexilift + 1}`}
                    className="carousel-image"
                  />
                  <div className="carousel-counter">
                    {activeImageIndex.lexilift + 1} / {projectImages.lexilift.length}
                  </div>
                </div>
                <button
                  className="carousel-btn next-btn"
                  onClick={() => nextImage("lexilift")}
                  data-testid="lexilift-next"
                >
                  ›
                </button>
              </div>
              <div className="work-links">
                <a
                  href="https://github.com/gershinho/windy_hacks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  📂 GitHub
                </a>
              </div>
            </div>

            {/* Orbital */}
            <div className="glass-card work-card project-card-enhanced" data-testid="orbital">
              <h3>Orbital Odysseys</h3>
              <p>Intergalactic travel agency providing the best plans to travel the galactic world.</p>
              <div className="tech-stack">
                <span className="tech-tag">Python Flask</span>
                <span className="tech-tag">HTML</span>
                <span className="tech-tag">CSS</span>
              </div>
              <div className="project-carousel">
                <button
                  className="carousel-btn prev-btn"
                  onClick={() => prevImage("orbital")}
                  data-testid="orbital-prev"
                >
                  ‹
                </button>
                <div className="carousel-image-container">
                  <img
                    src={projectImages.orbital[activeImageIndex.orbital]}
                    alt={`Orbital ${activeImageIndex.orbital + 1}`}
                    className="carousel-image"
                  />
                  <div className="carousel-counter">
                    {activeImageIndex.orbital + 1} / {projectImages.orbital.length}
                  </div>
                </div>
                <button
                  className="carousel-btn next-btn"
                  onClick={() => nextImage("orbital")}
                  data-testid="orbital-next"
                >
                  ›
                </button>
              </div>
              <div className="work-links">
                <a
                  href="https://github.com/gershinho/Orbital-Odysseys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  📂 GitHub
                </a>
              </div>
            </div>

            {/* DojoBrawl */}
            <div className="glass-card work-card project-card-enhanced" data-testid="dojobrawl">
              <h3>DojoBrawl</h3>
              <p>Street fighter style fighting game built in Python.</p>
              <div className="tech-stack">
                <span className="tech-tag">Pygame</span>
                <span className="tech-tag">Python</span>
              </div>
              <div className="project-video-container">
                <video
                  className="project-video"
                  controls
                  data-testid="dojobrawl-video"
                  playsInline
                  preload="metadata"
                >
                  <source src={`${PUB}/static/videos/DojoBrawl.mp4`} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                </div>

                <div className="work-links">
                  <a

                  href="https://github.com/gershinho/dojobrawl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  📂 GitHub
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Social Media */}
      {tab === "social" && (
        <section
          id="panel-social"
          role="tabpanel"
          aria-labelledby="tab-social"
          className="work-section"
        >
          <h2 className="work-category-title">📱 Social Media</h2>
          <div className="work-grid social-media-grid">
            <div className="glass-card work-card" data-testid="tiktok">
              <div className="work-image-container">
                <img
                   src={`${process.env.PUBLIC_URL}/static/img/tiktok.png`}
                  alt="TikTok"
                  className="work-image social-media-image"
                />
              </div>
              <h3>kptutorsat</h3>
              <p>
                SAT math and reading walkthroughs, tips for students to score 1500+, relatable SAT
                tiktoks, etc.
              </p>
              <div className="work-links">
                <a
                  href="https://www.tiktok.com/@kptutorsat?is_from_webapp=1&sender_device=pc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  👀 View on TikTok
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Music */}
      {tab === "music" && (
        <section
          id="panel-music"
          role="tabpanel"
          aria-labelledby="tab-music"
          className="work-section"
        >
          <h2 className="work-category-title">🎵 Music</h2>
          <div className="work-grid">
            <div className="glass-card work-card" data-testid="music-album">
              <img
                src={`${process.env.PUBLIC_URL}/static/img/album.png`}
                alt="Music Album"
                className="work-image"
              />
              <h3>Music Album for Cousin's Graduation — Summer 25'</h3>
              <p>16-year-old Krishna Patel's music album gift to Mansi Patel.</p>
              <p className="note">
                ***NOTE*** Disregard parental advisory image on album cover, ALL MUSIC IS KID FRIENDLY
              </p>
              <div className="work-links">
                <a
                  href="https://www.youtube.com/watch?v=ZgqzLJwoPgc&list=PLVMMQ00owvrHZNnaPLFkV3QU97HFoZJKt&pp=gAQB"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  ▶ Play on YouTube
                </a>
              </div>
            </div>

            <div className="glass-card work-card" data-testid="rap-music">
              <img
                src={`${process.env.PUBLIC_URL}/static/img/meandegg.png`}
                alt="Rap Music"
                className="work-image"
              />
              <h3>Rap Music from Middle School Years</h3>
              <p>Krishna's old kid-friendly rap music with bestfriend Eshaan.</p>
              <p className="note">***NOTE*** Cringey, listen at your own risk</p>
              <div className="work-links">
                <a
                  href="https://www.youtube.com/@lildonkxlilpigvids4807/videos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  ▶ Play on YouTube
                </a>
              </div>
            </div>

            <div className="glass-card work-card" data-testid="spotify-playlists">
              <img
                src={`${process.env.PUBLIC_URL}/static/img/spotify.png`}
                alt="Spotify Playlists"
                className="work-image"
              />
              <h3>Spotify Playlists — Dutch, Caribbean, Spanish, and More</h3>
              <p>Check out my love for foreign music through my perfectly curated playlists!</p>
              <div className="work-links">
                <a
                  href="https://open.spotify.com/user/zh6yigk3zphm8c7j7gv2ml2wg?si=26f528662c6f4e16"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  🎧 Listen on Spotify
                </a>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

const Recognition = () => {
  const awards = [
    {
      date: "April 2025",
      title: "Recognized on The Woodstock Independent for Navigo",
      organization: "The Woodstock Independent",
      description: "",
      link: "https://www.thewoodstockindependent.com/2025/04/whs-students-picked-for-google-showcase/",
    },
    {
      date: "March 2025",
      title: "2nd Place – Hack for the World",
      organization: "Naperville North Hackathon",
      description: "Vendi: A website giving exposure to food trucks and local businesses",
      link: "https://hackforthe.world/",
    },
    {
      date: "2025",
      title: "NHS President",
      organization: "National Honors Society",
      description: "",
      link: null,
    },
    {
      date: "2025",
      title:
        "National Recognition Program: School Recognition Award and First-Generation Award",
      organization: "College Board",
      description: "",
      link: null,
    },
    {
      date: "March 2024",
      title: "Judges' Award – Hack for the World",
      organization: "Naperville North Hackathon",
      description: "Orbital Odysseys: An Intergalactic travel agency",
      link: "https://hackforthe.world/",
    },
    {
      date: "2024",
      title: "Rural and Small-Town Recognition Award",
      organization: "College Board",
      description: "",
      link: null,
    },
  ];

  return (
    <div className="recognition-page">
      <div className="page-header">
        <Link to="/" className="back-link" data-testid="back-home-link">
          ← Back to Home
        </Link>
        <h1 className="page-title">Recognitions</h1>
        <p className="page-subtitle">Awards, Achievements & Honors</p>
      </div>

      <div className="awards-timeline">
        {awards.map((award, index) => (
          <div key={index} className="glass-card award-card" data-testid={`award-${index}`}>
            <div className="award-date">{award.date}</div>
            <h3 className="award-title">{award.title}</h3>
            <p className="award-org">{award.organization}</p>
            {award.description && <p className="award-desc">{award.description}</p>}
            {award.link && (
              <a href={award.link} target="_blank" rel="noopener noreferrer" className="btn-secondary">
                Learn More →
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Testimonials = () => {
  const allTestimonials = [
    {
      text:
        "As captain of the soccer team, Krishna led with heart and skill. Our team was made up of players from all kinds of backgrounds, and he had this rare ability to make everyone feel seen and valued. Under his leadership, the team grew more connected, disciplined, and confident both on and off the field.",
      author: "Coach Michael",
      role: "Varsity Soccer Coach",
    },
    {
      text:
        "What sets Krishna apart is his constant curiosity and drive to learn. He actively seeks new knowledge, asks insightful questions, and iterates with intention.",
      author: "Vikas Goyal",
      role: "Computer Science Mentor",
    },
    {
      text:
        "I had the pleasure of working with Krishna at our Kumon Math and Reading Center, where he consistently went above and beyond to help countless students of all backgrounds succeed. Krishna's ability to meet each student where they were, and then scaffold their learning to ensure understanding, made him an invaluable part of our team. His solution-focused approach and dedication to helping every student reach their academic goals set him apart. Krishna's exceptional tech skills and natural ability to adapt to diverse learning needs truly make him a standout individual.",
      author: "Roopa Desai",
      role: "Kumon Center Manager",
    },
    {
      text:
        "Krishna demonstrated professional curiosity and follow-through. He organized and conducted interviews with my ELL students to validate his app concept for airports, then synthesized those insights into practical design decisions. He's an eager entrepreneur with a strong user-research instinct.",
      author: "Bartholomew Labuz",
      role: "ELL teacher",
    },
  ];

  return (
    <div className="testimonials-page">
      <div className="page-header">
        <Link to="/" className="back-link" data-testid="back-home-link">
          ← Back to Home
        </Link>
        <h1 className="page-title">Testimonials</h1>
        <p className="page-subtitle">What people are saying about Krishna Patel</p>
      </div>

      <div className="testimonials-full-grid">
        {allTestimonials.map((testimonial, index) => (
          <div
            key={index}
            className="glass-card testimonial-full-card"
            data-testid={`full-testimonial-${index}`}
          >
            <div className="quote-icon-large">"</div>
            <p className="testimonial-text-large">{testimonial.text}</p>
            <div className="testimonial-footer">
              <p className="testimonial-author-large">{testimonial.author}</p>
              <p className="testimonial-role">{testimonial.role}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Navigation = () => {
  const location = useLocation();

  return (
    <nav className="navbar">
      <div className="nav-content">
        <Link to="/" className="logo" data-testid="logo-link">
          <span className="logo-text">Krishna Patel</span>
        </Link>
        <div className="nav-links">
          <Link
            to="/"
            className={`nav-link ${location.pathname === "/" ? "active" : ""}`}
            data-testid="nav-home"
          >
            Home
          </Link>
          <Link
            to="/my-work"
            className={`nav-link ${location.pathname === "/my-work" ? "active" : ""}`}
            data-testid="nav-work"
          >
            My Work
          </Link>
          <Link
            to="/recognition"
            className={`nav-link ${location.pathname === "/recognition" ? "active" : ""}`}
            data-testid="nav-recognition"
          >
            Recognition
          </Link>
          <Link
            to="/testimonials"
            className={`nav-link ${location.pathname === "/testimonials" ? "active" : ""}`}
            data-testid="nav-testimonials"
          >
            Testimonials
          </Link>
        </div>
      </div>
    </nav>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/my-work" element={<MyWork />} />
          <Route path="/recognition" element={<Recognition />} />
          <Route path="/testimonials" element={<Testimonials />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
